import { Alert, Button, Layout, Space, Tag } from 'antd';
import React, { useState } from 'react';

import {
    BackToModesLink,
    CITY_POOL,
    CORRECT_SCORE_THRESHOLD,
    CityNameWithCoatOfArms,
    EXTENDED_TOTAL_ROUNDS,
    Header,
    MapFrame,
    ModalMapPreview,
    Page,
    Question,
    QuizFinalResult,
    QuizPanel,
    RoundResultModal,
    RoundStatus,
    SvgMap,
    calculateBlindMapScore,
    calculateDistanceKm,
    formatDistance,
    pickRandom,
    useKrajFeatures,
} from '../../components/quiz';

const { Content } = Layout;

const makeQuestion = () => pickRandom(CITY_POOL);

const BlindMapGame = () => {
    const { features, error } = useKrajFeatures();
    const [round, setRound] = useState(1);
    const [score, setScore] = useState(0);
    const [city, setCity] = useState(makeQuestion);
    const [guessPoint, setGuessPoint] = useState(null);
    const [result, setResult] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [rounds, setRounds] = useState([]);
    const [finished, setFinished] = useState(false);

    const maxScore = EXTENDED_TOTAL_ROUNDS * 100;
    const targetPoint = result ? { lat: city.latitude, lon: city.longitude } : null;

    const restart = () => {
        setRound(1);
        setScore(0);
        setCity(makeQuestion());
        setGuessPoint(null);
        setResult(null);
        setModalVisible(false);
        setRounds([]);
        setFinished(false);
    };

    const submitGuess = () => {
        if (!guessPoint || result) return;
        const distance = calculateDistanceKm(guessPoint, { lat: city.latitude, lon: city.longitude });
        const roundScore = calculateBlindMapScore(distance);
        setResult({ distance, score: roundScore });
        setScore(currentScore => currentScore + roundScore);
        setRounds(prev => [
            ...prev,
            { correct: roundScore >= CORRECT_SCORE_THRESHOLD, score: roundScore, label: city.obec },
        ]);
        setModalVisible(true);
    };

    const advanceRound = () => {
        setModalVisible(false);
        setRound(r => r + 1);
        setCity(makeQuestion());
        setGuessPoint(null);
        setResult(null);
    };

    const showFinal = () => {
        setModalVisible(false);
        setFinished(true);
    };

    if (finished) {
        return (
            <QuizFinalResult score={score} totalRounds={EXTENDED_TOTAL_ROUNDS} rounds={rounds} onRestart={restart} />
        );
    }

    return (
        <>
            <QuizPanel>
                <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={EXTENDED_TOTAL_ROUNDS} />
                <Question>
                    <p>Slepá mapa obcí</p>
                    <h2>
                        <CityNameWithCoatOfArms city={city} size={58} />
                    </h2>
                    <p>Odhadni polohu obce kliknutím do mapy České republiky.</p>
                </Question>
                {error && <Alert type="error" message="Mapa se nepodařila načíst" description={error.message} />}
                <MapFrame>
                    {features ? (
                        <SvgMap
                            features={features}
                            guessPoint={guessPoint}
                            targetPoint={targetPoint}
                            onMapClick={result ? null : setGuessPoint}
                        />
                    ) : (
                        <Alert type="info" message="Načítám mapu..." />
                    )}
                </MapFrame>
                {!result && (
                    <Space wrap>
                        <Button type="primary" disabled={!guessPoint} onClick={submitGuess}>
                            Vyhodnotit
                        </Button>
                        {guessPoint && <Tag>Tip umístěn</Tag>}
                    </Space>
                )}
            </QuizPanel>
            <RoundResultModal
                visible={modalVisible}
                correct={result?.score >= CORRECT_SCORE_THRESHOLD}
                round={round}
                totalRounds={EXTENDED_TOTAL_ROUNDS}
                onNext={advanceRound}
                onShowResult={showFinal}
                wrongVerdict={result ? `${city.obec} jsi netrefil :(` : null}
            >
                <h3>Správná odpověď</h3>
                <p>
                    <strong style={{ fontSize: '1.2em' }}>{city.obec}</strong>
                    <br />
                    okres {city.okres}, {city.kraj}
                </p>
                {result && (
                    <p>
                        Tvůj tip byl <strong>{formatDistance(result.distance)}</strong> od správné polohy ·{' '}
                        <strong>{result.score}</strong> bodů za kolo.
                    </p>
                )}
                <ModalMapPreview features={features} guessPoint={guessPoint} targetPoint={targetPoint} />
            </RoundResultModal>
        </>
    );
};

export const BlindMap = () => (
    <Content>
        <Page>
            <Header>
                <BackToModesLink />
                <h1>Slepá mapa obcí</h1>
                <p>
                    Klikni do mapy a odhadni polohu české obce. Skóre určuje vzdálenost od správného místa. Pět kol,
                    pokaždé jiná obec.
                </p>
            </Header>
            <BlindMapGame />
        </Page>
    </Content>
);
